import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import './header.scss'
import { LanguageSwitch } from '../../LanguageSwitch'
import { useTranslation } from 'react-i18next'

interface Props {
  children: React.ReactNode
}

const navLeft = [
  { text: 'about', path: '/about' },
  { text: 'rooms', path: '/rooms' },
  { text: 'gallery', path: '/gallery' },
]

// Only shown in the mobile drawer — on desktop, "Nous contacter" (the CTA)
// already covers /contact, so a duplicate "Contact" nav link is redundant there.
const mobileOnly = [
  { text: 'contact us', path: '/contact' },
]

function AppLayout(props: Props) {
  const location = useLocation()
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileOpen) setMobileOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [mobileOpen])

  const handleLinkClick = (path: string) => {
    navigate(path)
    setMobileOpen(false)
    window.scrollTo(0, 0)
  }

  const renderNavItems = (items: typeof navLeft) =>
    items.map((item) => (
      <li key={item.path}>
        <a
          className={location.pathname === item.path ? 'active' : ''}
          onClick={() => handleLinkClick(item.path)}
        >
          {t(item.text)}
        </a>
      </li>
    ))

  return (
    <>
      <nav className={`navbar${scrolled ? ' scrolled' : ''}`}>
        <div className='navbar__inner'>
          <ul className='navbar__nav navbar__nav--left'>
            {renderNavItems(navLeft)}
          </ul>

          <a className='navbar__logo' onClick={() => handleLinkClick('/')}>
            <img className="navbar__logo-img" src={`/images/logo.png`} alt="Château Le Corvier" />
            <span className='navbar__logo-name'>Château Le Corvier</span>
          </a>

          <div className='navbar__right'>
            <a className='navbar__cta' onClick={() => handleLinkClick('/contact')}>{t('get in touch')}</a>
            <LanguageSwitch light />
          </div>

          <button
            className='navbar__mobile-toggle'
            aria-label='Menu'
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        <div className={`mobile-menu${mobileOpen ? ' open' : ''}`}>
          <a onClick={() => handleLinkClick('/')}>{t('home')}</a>
          {[...navLeft, ...mobileOnly].map((item) => (
            <a key={item.path} onClick={() => handleLinkClick(item.path)}>{t(item.text)}</a>
          ))}
        </div>
      </nav>
      <main>{props.children}</main>
    </>
  )
}

export default AppLayout
